// src/components/VendorCertificate.js

import React, { useEffect, useMemo, useState } from 'react';
import API from '../services/api';
import { Container, Spinner, Alert, Button, Badge } from 'react-bootstrap';
import logo from '../assets/logo.png';
import './VendorCertificate.css';
import { formatVendorId } from '../utils/idFormat';

const VendorCertificate = () => {
  const [user, setUser] = useState(null);
  const [vendorProfile, setVendorProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchVendor = async () => {
    try {
      const [userRes, profileRes] = await Promise.all([
        API.get('/auth/me'),
        API.get('/vendor/profile').catch(() => ({ data: null })),
      ]);
      setUser(userRes.data || null);
      setVendorProfile(profileRes.data || null);
    } catch (err) {
      setError('Error loading certificate');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVendor();
  }, []);

  const displayName = useMemo(
    () => vendorProfile?.companyName || user?.fullName || 'Vendor',
    [vendorProfile, user]
  );
  const verificationCode = useMemo(() => {
    const baseName = (user?.fullName || vendorProfile?.companyName || 'VENDOR')
      .trim()
      .split(/\s+/)[0]
      .toUpperCase();
    const mobile = String(vendorProfile?.contactMobile || user?.mobile || '')
      .replace(/\D/g, '')
      .slice(0, 5) || '00000';
    return `${baseName}-${mobile}-AUTH`;
  }, [user, vendorProfile]);
  const vendorId = useMemo(
    () => formatVendorId(vendorProfile || {}, user || {}),
    [vendorProfile, user]
  );
  const issuedAt = useMemo(() => {
    if (user?.certificateIssuedAt) return new Date(user.certificateIssuedAt);
    return new Date();
  }, [user]);

  const buildCertificateHtml = () => `
    <!DOCTYPE html>
    <html lang="en">
      <head>
        <meta charset="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <title>Vendor Authorization Certificate</title>
        <link href="https://fonts.googleapis.com/css2?family=Newsreader:ital,opsz,wght@0,6..72,200..800;1,6..72,200..800&family=Noto+Sans:wght@400;500;700&display=swap" rel="stylesheet" />
        <style>
          :root {
            --primary: #17cf36;
            --bg: #f6f8f6;
            --parchment: #fdfcf8;
            --border-green: #0e6b1d;
            --text: #111812;
            --muted: #6b7280;
          }
          * { box-sizing: border-box; }
          body {
            margin: 0;
            background: var(--bg);
            font-family: "Newsreader", serif;
            color: var(--text);
          }
          .page { min-height: 100vh; display: flex; flex-direction: column; }
          header {
            padding: 16px 32px;
            background: #fff;
            border-bottom: 1px solid #f0f4f1;
            display: flex;
            justify-content: space-between;
            align-items: center;
            position: sticky;
            top: 0;
          }
          header h2 { margin: 0; font-family: "Noto Sans", sans-serif; font-size: 16px; }
          main {
            flex: 1;
            display: flex;
            flex-direction: column;
            align-items: center;
            padding: 40px 16px 64px;
          }
          .print-area {
            width: 100%;
            max-width: 900px;
            background: var(--parchment);
            position: relative;
            padding: 48px;
            border: 12px solid var(--border-green);
            box-shadow: 0 30px 60px rgba(0,0,0,0.15);
          }
          .watermark {
            position: absolute;
            inset: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            opacity: 0.03;
            pointer-events: none;
          }
          .content {
            position: relative;
            z-index: 1;
            display: flex;
            flex-direction: column;
            align-items: center;
            text-align: center;
            gap: 20px;
          }
          .brand { display: flex; flex-direction: column; align-items: center; gap: 6px; }
          .brand-row {
            display: flex;
            align-items: center;
            gap: 10px;
            font-family: "Noto Sans", sans-serif;
            font-weight: 700;
            letter-spacing: 0.2em;
            color: var(--border-green);
            text-transform: uppercase;
            font-size: 14px;
          }
          .brand-row img { height: 48px; }
          .subtitle {
            font-family: "Noto Sans", sans-serif;
            font-size: 10px;
            letter-spacing: 0.2em;
            color: #6b7280;
            text-transform: uppercase;
          }
          .title {
            font-size: 46px;
            color: var(--border-green);
            font-style: italic;
            margin: 10px 0 4px;
          }
          .divider { width: 120px; height: 4px; background: rgba(23, 207, 54, 0.2); margin: 6px auto 0; }
          .recipient p { font-family: "Noto Sans", sans-serif; color: #6b7280; margin: 0 0 10px; }
          .recipient h2 {
            margin: 0 0 8px;
            font-size: 32px;
            font-family: "Noto Sans", sans-serif;
            text-decoration: underline;
            text-decoration-color: rgba(23, 207, 54, 0.2);
            text-underline-offset: 8px;
          }
          .recipient .desc { max-width: 640px; font-style: italic; color: #374151; line-height: 1.6; }
          .details {
            width: 100%;
            display: grid;
            grid-template-columns: repeat(4, minmax(0, 1fr));
            gap: 16px;
            border-top: 1px solid rgba(23, 207, 54, 0.15);
            border-bottom: 1px solid rgba(23, 207, 54, 0.15);
            padding: 18px 0;
          }
          .detail-label {
            font-family: "Noto Sans", sans-serif;
            font-size: 10px;
            color: var(--primary);
            letter-spacing: 0.2em;
            text-transform: uppercase;
            font-weight: 700;
          }
          .detail-value { font-family: "Noto Sans", sans-serif; font-size: 13px; font-weight: 700; }
          .footer {
            width: 100%;
            display: flex;
            justify-content: space-between;
            align-items: center;
          }
          .signature { text-align: right; }
          .signature .name { font-family: "Noto Sans", sans-serif; font-weight: 700; }
          .signature .line {
            height: 1px;
            width: 160px;
            background: rgba(23, 207, 54, 0.35);
            margin: 8px 0 6px auto;
          }
          .corners span {
            position: absolute;
            width: 80px;
            height: 80px;
            border: 4px solid rgba(23, 207, 54, 0.2);
          }
          .corners .tl { top: 8px; left: 8px; border-right: 0; border-bottom: 0; }
          .corners .tr { top: 8px; right: 8px; border-left: 0; border-bottom: 0; }
          .corners .bl { bottom: 8px; left: 8px; border-right: 0; border-top: 0; }
          .corners .br { bottom: 8px; right: 8px; border-left: 0; border-top: 0; }
          @media print {
            header { display: none !important; }
            body { background: #fff !important; }
            .print-area { box-shadow: none !important; border-width: 8px; }
          }
          @media (max-width: 720px) {
            .print-area { padding: 32px; }
            .details { grid-template-columns: repeat(2, minmax(0, 1fr)); }
            .footer { flex-direction: column; gap: 18px; align-items: flex-start; }
            .signature { text-align: left; }
            .signature .line { margin-left: 0; }
          }
        </style>
      </head>
      <body>
        <div class="page">
          <header>
            <h2>Lotus Rice Mill Pvt. Ltd.</h2>
            <div>
              <button onclick="window.print()" style="margin-right:8px;padding:8px 12px;border:none;border-radius:8px;background:var(--primary);color:#fff;font-weight:700;cursor:pointer;">Print</button>
              <button onclick="window.print()" style="padding:8px 12px;border:1px solid #e5e7eb;border-radius:8px;background:#fff;color:#111812;font-weight:700;cursor:pointer;">PDF</button>
            </div>
          </header>
          <main>
            <div class="print-area">
              <div class="watermark">
                <svg width="420" height="420" viewBox="0 0 48 48" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12.0799 24L4 19.2479L9.95537 8.75216L18.04 13.4961L18.0446 4H29.9554L29.96 13.4961L38.0446 8.75216L44 19.2479L35.92 24L44 28.7521L38.0446 39.2479L29.96 34.5039L29.9554 44H18.0446L18.04 34.5039L9.95537 39.2479L4 28.7521L12.0799 24Z"></path>
                </svg>
              </div>
              <div class="content">
                <div class="brand">
                  <div class="brand-row">
                    <img src="${logo}" alt="Lotus Rice Mill Pvt. Ltd." />
                    Lotus Rice Mill Pvt. Ltd.
                  </div>
                  <div class="subtitle">Excellence in Agriculture Since 1985</div>
                </div>
                <div>
                  <div class="title">Certificate of Authorization</div>
                  <div class="divider"></div>
                </div>
                <div class="recipient">
                  <p>This is to officially certify that</p>
                  <h2>${displayName}</h2>
                  <p class="desc">
                    has been duly vetted and is hereby recognized as an authorized distribution partner for all luxury grain products under the Lotus Rice Mill Pvt. Ltd. banner.
                  </p>
                </div>
                <div class="details">
                  <div>
                    <div class="detail-label">Vendor ID</div>
                    <div class="detail-value">${vendorId}</div>
                  </div>
                  <div>
                    <div class="detail-label">GSTIN</div>
                    <div class="detail-value">${vendorProfile?.gstNumber || 'N/A'}</div>
                  </div>
                  <div>
                    <div class="detail-label">Verification</div>
                    <div class="detail-value">${verificationCode}</div>
                  </div>
                  <div>
                    <div class="detail-label">Contact Email</div>
                    <div class="detail-value">${vendorProfile?.contactEmail || user?.email || 'N/A'}</div>
                  </div>
                </div>
                <div class="footer">
                  <div>
                    <div class="detail-label">Date of Issue</div>
                    <div class="detail-value">${issuedAt.toLocaleDateString()}</div>
                  </div>
                  <div class="signature">
                    <div class="name">Anil Saxena</div>
                    <div class="line"></div>
                    <div class="detail-label">Authorized Signatory</div>
                  </div>
                </div>
                <div class="corners">
                  <span class="tl"></span>
                  <span class="tr"></span>
                  <span class="bl"></span>
                  <span class="br"></span>
                </div>
              </div>
            </div>
          </main>
        </div>
      </body>
    </html>
  `;

  const openPrintable = () => {
    const win = window.open('', '_blank');
    if (!win) return;
    win.document.write(buildCertificateHtml());
    win.document.close();
  };

  if (loading) {
    return (
      <Container className="mt-4 text-center">
        <Spinner animation="border" />
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="mt-4">
        <Alert variant="danger">{error}</Alert>
      </Container>
    );
  }

  const certStatus = user?.certificateStatus || 'pending';
  const isGenerated = certStatus === 'generated';
  const statusBadge = () => {
    if (certStatus === 'generated') return <Badge bg="success">Verified</Badge>;
    if (certStatus === 'rejected') return <Badge bg="danger">Rejected</Badge>;
    return <Badge bg="warning">Pending</Badge>;
  };

  return (
    <Container className="mt-4 vendor-cert-page">
      <div className="vendor-cert-header">
        <div>
          <h4 className="mb-1">Vendor Authorization Certificate</h4>
          <p className="vendor-cert-subtitle">Your official verification document issued by Lotus Rice Mill Pvt. Ltd.</p>
        </div>
        <div className="vendor-cert-actions">
          {statusBadge()}
          <Button variant="outline-success" onClick={openPrintable} disabled={!isGenerated}>
            Print / Save PDF
          </Button>
        </div>
      </div>

      <div className="vendor-cert-preview">
        <div className="vendor-cert-paper">
          <div className="vendor-cert-watermark">
            <svg width="360" height="360" viewBox="0 0 48 48" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
              <path d="M12.0799 24L4 19.2479L9.95537 8.75216L18.04 13.4961L18.0446 4H29.9554L29.96 13.4961L38.0446 8.75216L44 19.2479L35.92 24L44 28.7521L38.0446 39.2479L29.96 34.5039L29.9554 44H18.0446L18.04 34.5039L9.95537 39.2479L4 28.7521L12.0799 24Z"></path>
            </svg>
          </div>
          <div className="vendor-cert-content">
            <div className="vendor-cert-brand">
              <div className="vendor-cert-brand-row">
                <img src={logo} alt="Lotus Rice Mill Pvt. Ltd." />
                Lotus Rice Mill Pvt. Ltd.
              </div>
              <div className="vendor-cert-subline">Excellence in Agriculture Since 1985</div>
            </div>
            <div>
              <div className="vendor-cert-title">Certificate of Authorization</div>
              <div className="vendor-cert-divider"></div>
            </div>
            <div className="vendor-cert-recipient">
              <p>This is to officially certify that</p>
              <h2>{displayName}</h2>
              <p className="desc">
                has been duly vetted and is hereby recognized as an authorized distribution partner for all luxury grain products under the Lotus Rice Mill Pvt. Ltd. banner.
              </p>
            </div>
            <div className="vendor-cert-details">
              <div>
                <div className="detail-label">Vendor ID</div>
                <div className="detail-value">{vendorId}</div>
              </div>
              <div>
                <div className="detail-label">GSTIN</div>
                <div className="detail-value">{vendorProfile?.gstNumber || 'N/A'}</div>
              </div>
              <div>
                <div className="detail-label">Verification</div>
                <div className="detail-value">{verificationCode}</div>
              </div>
              <div>
                <div className="detail-label">Contact Email</div>
                <div className="detail-value">{vendorProfile?.contactEmail || user?.email || 'N/A'}</div>
              </div>
            </div>
            <div className="vendor-cert-footer">
              <div>
                <div className="detail-label">Date of Issue</div>
                <div className="detail-value">{issuedAt.toLocaleDateString()}</div>
              </div>
              <div className="vendor-cert-signature">
                <div className="name">Anil Saxena</div>
                <div className="line"></div>
                <div className="detail-label">Authorized Signatory</div>
              </div>
            </div>
            <div className="vendor-cert-corners">
              <span className="tl"></span>
              <span className="tr"></span>
              <span className="bl"></span>
              <span className="br"></span>
            </div>
          </div>
        </div>
      </div>

      {!isGenerated && (
        <div className="text-muted mt-3">
          {certStatus === 'rejected'
            ? 'Certificate request was rejected. Please contact admin.'
            : 'Certificate is pending admin approval.'}
        </div>
      )}
    </Container>
  );
};

export default VendorCertificate;
